import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams } from '@/components/DataTable';
import type {
  BulkToolbarContext,
  RowSelectionOptions,
} from '@/components/DataTable/types';
import type {
  AlertVisibility,
  GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import { queryKeys } from '@/hooks/api/queryKeys';
import { AlertVisibilitySelector } from '@/components/AlertVisibilitySelector';
import { useAlertVisibility } from '@/hooks/useAlertVisibility';
import { useAlertsIdeColumnEnabled } from '@/hooks/useAlertsIdeColumnEnabled';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { sanitizeAlertsParams } from '@/components/DataTable/alerts/sanitizeAlertsParams';
import { useTimestampFormat } from '@/hooks/useTimestampFormat';
import { AlertsBulkActionBar } from '@/components/AlertsBulkActions/AlertsBulkActionBar';
import {
  createUserAlertsColumns,
  createUserAlertsActionsColumn,
} from './columns';
import { fetchUserAlerts } from './api';
import { TableRowExpanded } from './TableRowExpanded';

const ACCOUNT_SCOPE_KEY = 'account';

/**
 * Build the query key for the user alerts page. `visibility` is appended
 * only when it is not `'active'` so toggling away from the default
 * triggers a refetch instead of returning the previously-cached
 * default-list page. The default value is omitted entirely so the cache
 * key for the default-list baseline stays byte-identical to the
 * pre-KIR-2512 wire shape.
 */
function getQueryKeysFromParams(
  params: DataFetchParams,
  visibility: AlertVisibility
) {
  const baseKey = queryKeys.alerts.listFiltered({
    page: params.pagination.pageIndex + 1,
    limit: params.pagination.pageSize,
    filters: params.filters,
    sortBy: params.sort?.field,
    sortOrder: params.sort?.order,
    search: params.search,
  });
  if (visibility === 'active') return baseKey;
  return [...baseKey, { visibility }];
}

/**
 * DataTable-based alerts tab for the user dashboard.
 */
export function DataTableUserAlertsContent(): ReactElement {
  const ideColumnEnabled = useAlertsIdeColumnEnabled();
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS,
    flagsmithFeatureFlags.ALERT_MUTING,
    flagsmithFeatureFlags.ALERT_IGNORE,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const editPolicyEnabled =
    flags[flagsmithFeatureFlags.EDIT_POLICY_FROM_ALERTS] ?? false;
  const mutingEnabled = flags[flagsmithFeatureFlags.ALERT_MUTING] === true;
  const ignoreEnabled = flags[flagsmithFeatureFlags.ALERT_IGNORE] === true;

  const { visibility, setVisibility, effectiveVisibility, anyEnabled } =
    useAlertVisibility(ACCOUNT_SCOPE_KEY);

  // Per-user Timestamp Format → format-aware Last/First Seen column widths;
  // recreate the columns when the format toggles.
  const { value: timestampFormat } = useTimestampFormat();

  // Append the Actions column only when a suppression flag (mute/ignore, via
  // `anyEnabled`) is on. The "Add to Allow/Block List" action (KIR-3558) lives
  // in the expanded row's "DETECTED ON CLIENT" panel, not this column.
  const columns = useMemo(() => {
    const base = createUserAlertsColumns(
      ideColumnEnabled,
      revampEnabled,
      timestampFormat
    );
    if (!anyEnabled && !editPolicyEnabled) return base;
    return [...base, createUserAlertsActionsColumn()];
  }, [
    anyEnabled,
    editPolicyEnabled,
    ideColumnEnabled,
    revampEnabled,
    timestampFormat,
  ]);

  const searchConfig = {
    placeholder: 'Search alerts',
    className: 'w-[260px]',
    inputProps: { 'aria-label': 'Search alerts' },
  };

  const sanitizeParams = useCallback(
    (params: DataFetchParams): DataFetchParams =>
      sanitizeAlertsParams(params, ideColumnEnabled),
    [ideColumnEnabled]
  );

  const queryFn = useCallback(
    (params: DataFetchParams, signal?: AbortSignal) =>
      fetchUserAlerts(
        sanitizeParams(params),
        signal,
        effectiveVisibility === 'active' ? undefined : effectiveVisibility
      ),
    [effectiveVisibility, sanitizeParams]
  );

  const queryKeyFn = useCallback(
    (params: DataFetchParams) =>
      getQueryKeysFromParams(sanitizeParams(params), effectiveVisibility),
    [effectiveVisibility, sanitizeParams]
  );

  // Visibility affects total count — include it in the knownTotal fingerprint
  // so toggling visibility invalidates the cached total.
  const countScope = useMemo(
    () => ({ visibility: effectiveVisibility }),
    [effectiveVisibility]
  );

  // Dim a row when ANY suppression state is set. See the org container for
  // the full rationale on why OR is the right combinator here AND why the
  // dim is scoped to non-Actions cells via the `[&>td:not(:last-child)]`
  // arbitrary variant rather than applied at row-level (short version: CSS
  // opacity cascades to all descendants and would dim the
  // Mute/Unmute/Create-Exclusion/Remove-Exclusion buttons, making them
  // appear unavailable when they are in fact the primary affordance to
  // un-suppress the row).
  const getRowClassName = useCallback(
    (row: GroupedAlert): string | undefined =>
      row.mute?.muted || row.exclude?.excluded
        ? '[&>td:not(:last-child)]:opacity-60'
        : undefined,
    []
  );

  // Bulk selection (KIR-3252, personal scope). Gated ONLY by the suppression
  // flags — NO role gate: personal alerts are the user's own data, so unlike
  // the org tab there is no owner/manager check. `scope: 'account'` routes the
  // shared bulk bar to the `/account/alerts/bulk/*` endpoints.
  const bulkSelectionEnabled = mutingEnabled || ignoreEnabled;
  const rowSelectionOptions = useMemo<
    RowSelectionOptions<GroupedAlert> | undefined
  >(
    () =>
      bulkSelectionEnabled
        ? {
            getRowId: (row: GroupedAlert) => row.alertKey,
            // Tabs (Active/All/Suppressed) swap the list without touching
            // filters/search, so feed visibility in as the reset token to clear
            // the selection on tab change.
            resetToken: effectiveVisibility,
            renderBulkToolbar: (ctx: BulkToolbarContext<GroupedAlert>) => (
              <AlertsBulkActionBar
                ctx={ctx}
                scope={{ kind: 'account' }}
                mutingEnabled={mutingEnabled}
                ignoreEnabled={ignoreEnabled}
                visibility={effectiveVisibility}
              />
            ),
          }
        : undefined,
    [bulkSelectionEnabled, mutingEnabled, ignoreEnabled, effectiveVisibility]
  );

  // KIR-2748: keep the visibility bar + table inside ONE viewport-constrained,
  // overflow-hidden flex column so the bar consumes part of the fixed height
  // budget instead of stacking on top of the table's height and pushing the
  // page column past 100vh (the outer-scroll bug). The DataTable flexes to fill
  // the remaining space (`flex-1 min-h-0`) and keeps its own inner overflow
  // scroll, so any table overflow stays inside the table, never the page.
  return (
    <div className="flex flex-col h-[calc(100vh-11rem)] overflow-hidden">
      {anyEnabled && (
        <div className="flex items-center justify-end gap-3 px-4 py-2 shrink-0">
          <AlertVisibilitySelector
            scopeKey={ACCOUNT_SCOPE_KEY}
            value={visibility}
            onChange={setVisibility}
          />
        </div>
      )}
      {/*
        ⚠️ KIR-2659 deep-link contract: the `id="user-alt"` value is
        also baked into URLs constructed by the desktop agent + IDE
        extension when they build summary-banner "View Details" deep
        links (see extensions/agent/pkg/notifications/coordinator.go
        and extensions/vscode-core/src/services/notifications/
        coordinator.ts — both ship URLs like
        `/dashboard/alerts?user-alt.sev[includes]=high&...`).
        Renaming this id breaks every notification deep-link to the
        personal alerts view. Update the agent + extension URL
        builders in the same PR if you change it.
      */}
      <DataTable<GroupedAlert>
        id="user-alt"
        mode="server"
        className="flex flex-col flex-1 min-h-0"
        containerClassName="flex-1"
        columns={columns}
        errorBoundaryLabel="Something went wrong in the Alerts table"
        search={searchConfig}
        features={{ enableRowExpansion: true, enableSorting: true }}
        rowExpansion={{
          icon: <ChevronRight className="h-4 w-4" />,
          render: row => <TableRowExpanded row={row} />,
          contentClassName: 'bg-muted/30 p-4',
          rowClassName: 'bg-muted/10',
          keepMounted: false,
          showContainer: false,
        }}
        queryFn={queryFn}
        getQueryKeysFromParams={queryKeyFn}
        countScope={countScope}
        rowSelection={rowSelectionOptions}
        getRowClassName={anyEnabled ? getRowClassName : undefined}
        noDataLabel="No alerts found."
        queryConfig={{
          staleTime: 30000,
          refetchOnWindowFocus: false,
        }}
      />
    </div>
  );
}

DataTableUserAlertsContent.displayName = 'DataTableUserAlertsContent';
